// Layer 3 seed: claims, authority, rulings, and model versions for Bunker Hill
// (corpus register D1–D8 / S1–S16 — docs/audits/2026-08-19-tim-scope-reconciliation.md §2.4).

import type {
  AuthorityRule,
  Claim,
  ResolvedVersion,
  Ruling,
} from '@/versions/v1/schema/claim';
import { resolve, subjectKey } from '@/versions/v1/shared/claims';

export const AUTHORITY: readonly AuthorityRule[] = [
  {
    kind: 'structure',
    ranking: ['pid', 'flowsheet', 'dcs-fd'],
    note: 'Connectivity — P&ID beats mass balance beats FD narrative',
  },
  {
    kind: 'identity',
    ranking: ['io-list', 'dcs-fd', 'pid'],
    note: 'Tag identity — I/O list beats FD beats P&ID',
  },
  {
    kind: 'design-basis',
    ranking: ['mass-balance', 'flowsheet', 'dcs-fd'],
    note: 'Design quantities — mass balance beats flowsheet beats FD',
  },
  {
    kind: 'envelope',
    ranking: ['dcs-fd', 'mass-balance'],
    note: 'Equipment envelope — FD Appendix B',
  },
  {
    kind: 'verification',
    ranking: ['mass-balance'],
    note: 'Verification — mass balance',
  },
  {
    kind: 'failure-mode',
    ranking: ['fmea'],
    note: 'Failure-mode — FMEA',
  },
];

const doc = (
  sourceId: string,
  sourceKind: 'mass-balance' | 'flowsheet' | 'dcs-fd' | 'io-list' | 'fmea',
  revision?: string,
  locator?: string,
) =>
  ({
    tier: 'document',
    sourceId,
    sourceKind,
    revision,
    locator,
  }) as const;

export const CLAIMS: readonly Claim[] = [
  // D1 / S1 — Pb concentrate grade
  {
    id: 'cl-pb-grade-spec',
    kind: 'design-basis',
    subject: { kind: 'stream-spec', id: 'st-pbcon', field: 'pb-grade' },
    value: 67.0,
    valueLabel: '67.0% Pb',
    source: doc('src-balance', 'mass-balance', 'rev 1', '§8.4 spec'),
    vintage: '2026-05',
    status: 'disputed',
    conflictsWith: ['cl-pb-grade-deck'],
    provenance: 'D1 · S1 — module doc spec vs deck design column',
  },
  {
    id: 'cl-pb-grade-deck',
    kind: 'design-basis',
    subject: { kind: 'stream-spec', id: 'st-pbcon', field: 'pb-grade' },
    value: 65.0,
    valueLabel: '65.0% Pb',
    source: doc('src-pfd', 'flowsheet', 'approved'),
    vintage: '2026-05',
    status: 'disputed',
    conflictsWith: ['cl-pb-grade-spec'],
    provenance: 'D1 · S1 — Greg deck design column',
  },
  {
    id: 'cl-pb-grade-report',
    kind: 'design-basis',
    subject: { kind: 'stream-spec', id: 'st-pbcon', field: 'pb-grade' },
    value: 63.4,
    valueLabel: '63.4% Pb',
    source: doc('src-balance', 'mass-balance', 'rev 2', 'report example'),
    vintage: '2026-06',
    status: 'disputed',
    conflictsWith: ['cl-pb-grade-deck', 'cl-pb-grade-spec'],
    provenance: 'D1 — C3 optiflow-ui report example',
  },
  // D2 — Zn concentrate grade
  {
    id: 'cl-zn-grade-spec',
    kind: 'design-basis',
    subject: { kind: 'stream-spec', id: 'st-zncon', field: 'zn-grade' },
    value: 58.0,
    valueLabel: '58.0% Zn',
    source: doc('src-balance', 'mass-balance', 'rev 1'),
    vintage: '2026-05',
    status: 'disputed',
    conflictsWith: ['cl-zn-grade-deck'],
    provenance: 'D2 · S1 — module doc spec',
  },
  {
    id: 'cl-zn-grade-deck',
    kind: 'design-basis',
    subject: { kind: 'stream-spec', id: 'st-zncon', field: 'zn-grade' },
    value: 52.0,
    valueLabel: '52.0% Zn',
    source: doc('src-pfd', 'flowsheet'),
    vintage: '2026-05',
    status: 'disputed',
    conflictsWith: ['cl-zn-grade-spec'],
    provenance: 'D2 · S1 — deck design column',
  },
  // D3 — Recovery targets (design column only; ramp is a Target basis, not a rival claim)
  {
    id: 'cl-zn-rec-deck',
    kind: 'design-basis',
    subject: { kind: 'kpi', id: 'zn-recovery', field: 'design' },
    value: 90.0,
    valueLabel: 'Zn 90%',
    source: doc('src-pfd', 'flowsheet'),
    vintage: '2026-05',
    status: 'disputed',
    provenance: 'D3 — deck design recovery',
  },
  {
    id: 'cl-pb-rec-deck',
    kind: 'design-basis',
    subject: { kind: 'kpi', id: 'pb-recovery', field: 'design' },
    value: 87.0,
    valueLabel: 'Pb 87%',
    source: doc('src-pfd', 'flowsheet'),
    vintage: '2026-05',
    status: 'disputed',
    conflictsWith: ['cl-pb-rec-report'],
    provenance: 'D3 — deck design Pb recovery (S1 design column)',
  },
  {
    id: 'cl-pb-rec-report',
    kind: 'design-basis',
    subject: { kind: 'kpi', id: 'pb-recovery', field: 'design' },
    value: 84.0,
    valueLabel: 'Pb 84%',
    source: doc('src-balance', 'mass-balance', 'rev 2', 'C3 report'),
    vintage: '2026-06',
    status: 'disputed',
    conflictsWith: ['cl-pb-rec-deck'],
    provenance: 'D3 — C3 Optiflow-UI report examples (corpus 84% Pb)',
  },
  // seed.P4 / #293 — PFS design basis from Paul's module document (seed.P1 #258)
  {
    id: 'cl-pb-rec-pfs',
    kind: 'design-basis',
    subject: { kind: 'kpi', id: 'pb-recovery', field: 'design' },
    value: 88.2,
    valueLabel: 'Pb 88.2%',
    source: doc('src-dcs-fd', 'dcs-fd', undefined, 'A §M1 — PFS design basis'),
    vintage: '2026-05',
    status: 'accepted',
    provenance: 'A §M1 — PFS design basis (seed.P1 #258 · seed.P4 #293)',
  },
  {
    id: 'cl-zn-rec-pfs',
    kind: 'design-basis',
    subject: { kind: 'kpi', id: 'zn-recovery', field: 'design' },
    value: 85.1,
    valueLabel: 'Zn 85.1%',
    source: doc('src-dcs-fd', 'dcs-fd', undefined, 'A §M1 — PFS design basis'),
    vintage: '2026-05',
    status: 'accepted',
    provenance: 'A §M1 — PFS design basis (seed.P1 #258 · seed.P4 #293)',
  },
  {
    id: 'cl-ag-rec-pfs',
    kind: 'design-basis',
    subject: { kind: 'kpi', id: 'ag-recovery', field: 'design' },
    value: 84.2,
    valueLabel: 'Ag 84.2%',
    source: doc('src-dcs-fd', 'dcs-fd', undefined, 'A §M1 — PFS design basis'),
    vintage: '2026-05',
    status: 'accepted',
    provenance: 'A §M1 — PFS design basis (seed.P1 #258 · seed.P4 #293)',
  },
  {
    id: 'cl-ag-grade-pfs',
    kind: 'design-basis',
    subject: { kind: 'stream-spec', id: 'st-pbcon', field: 'ag-grade' },
    value: 12.13,
    valueLabel: '12.13 oz/t Ag',
    source: doc('src-dcs-fd', 'dcs-fd', undefined, 'A §M1 — PFS design basis'),
    vintage: '2026-05',
    status: 'accepted',
    provenance: 'A §M1 — PFS design basis (seed.P1 #258 · seed.P4 #293)',
  },
  // D4 / S2 — Grind P80 and regrind
  {
    id: 'cl-p80-sa01',
    kind: 'design-basis',
    subject: { kind: 'stream-spec', id: 'st-cyc-of', field: 'p80' },
    value: 74,
    valueLabel: 'P80 74 µm',
    source: doc('src-dcs-fd', 'dcs-fd', undefined, 'SA01 spec'),
    vintage: '2026-05',
    status: 'disputed',
    conflictsWith: ['cl-p80-deck'],
    provenance: 'D4 — FD SA01 spec',
  },
  {
    id: 'cl-p80-deck',
    kind: 'design-basis',
    subject: { kind: 'stream-spec', id: 'st-cyc-of', field: 'p80' },
    value: 75,
    valueLabel: 'P80 75 µm',
    source: doc('src-pfd', 'flowsheet'),
    vintage: '2026-05',
    status: 'disputed',
    conflictsWith: ['cl-p80-sa01'],
    provenance: 'D4 — deck design grind',
  },
  {
    id: 'cl-regrind-c2',
    kind: 'design-basis',
    subject: { kind: 'stream-spec', id: 'st-pbcon', field: 'regrind-p80' },
    value: 30,
    valueLabel: 'P80 ~30 µm',
    source: doc('src-dcs-fd', 'dcs-fd', undefined, 'UI concept'),
    vintage: '2026-05',
    status: 'disputed',
    conflictsWith: ['cl-regrind-deck'],
    provenance: 'D4 · S2 — concentrator UI concept',
  },
  {
    id: 'cl-regrind-deck',
    kind: 'design-basis',
    subject: { kind: 'stream-spec', id: 'st-pbcon', field: 'regrind-p80' },
    value: 38,
    valueLabel: 'P80 38 µm SP 35–42',
    source: doc('src-pfd', 'flowsheet'),
    vintage: '2026-05',
    status: 'disputed',
    conflictsWith: ['cl-regrind-c2'],
    provenance: 'D4 · S2 — deck live setpoint',
  },
  // D5 / S3 — Tag dialect (complementary mappings, not a won/lost conflict)
  {
    id: 'cl-tag-lic4010',
    kind: 'identity',
    subject: { kind: 'tag', id: 'pb-rougher-level', field: 'loop-tag' },
    value: 'LIC4010',
    valueLabel: 'LIC4010',
    source: doc('src-dcs-fd', 'dcs-fd', undefined, 'Appendix B'),
    vintage: '2026-05',
    status: 'disputed',
    provenance: 'D5 · S3 — FD loop tag',
  },
  {
    id: 'cl-tag-lt140',
    kind: 'identity',
    subject: { kind: 'tag', id: 'pb-rougher-level', field: 'instrument-tag' },
    value: 'LT-140',
    valueLabel: 'LT-140 / LIT4010',
    source: doc('src-fmea', 'fmea'),
    vintage: '2026-05',
    status: 'disputed',
    provenance: 'D5 · S3 — FMEA instrument tag',
  },
  // D6 / S4 — Cyclone spare capacity
  {
    id: 'cl-cyc-spec',
    kind: 'envelope',
    subject: { kind: 'constraint', id: 'u-cyc', field: 'in-service' },
    value: 4,
    valueLabel: '4 of 5 in service · 1 spare',
    source: doc('src-dcs-fd', 'dcs-fd', undefined, '§8.2'),
    vintage: '2026-05',
    status: 'disputed',
    conflictsWith: ['cl-cyc-check'],
    provenance: 'D6 · S4 — module doc cyclone duty',
  },
  {
    id: 'cl-cyc-check',
    kind: 'envelope',
    subject: { kind: 'constraint', id: 'u-cyc', field: 'in-service' },
    value: 5.7,
    valueLabel: '~5.7 cyclones at stated duty',
    source: doc('src-dcs-fd', 'dcs-fd', undefined, 'UI concept aside'),
    vintage: '2026-05',
    status: 'disputed',
    conflictsWith: ['cl-cyc-spec'],
    provenance: 'D6 · S4 — UI concept duty check',
  },
  // D8 / S6 — Sampling inventory (complementary breadth vs authoritative core)
  {
    id: 'cl-samples-fd',
    kind: 'verification',
    subject: { kind: 'sample-point', id: 'inventory', field: 'fd-core' },
    value: 7,
    valueLabel: 'SA01–SA07',
    source: doc('src-dcs-fd', 'dcs-fd', undefined, '§14.2'),
    vintage: '2026-05',
    status: 'disputed',
    provenance: 'D8 — FD authoritative core (S6)',
  },
  {
    id: 'cl-samples-deck',
    kind: 'verification',
    subject: { kind: 'sample-point', id: 'inventory', field: 'deck-register' },
    value: 12,
    valueLabel: '12 register streams',
    source: doc('src-pfd', 'flowsheet', undefined, 'deck register'),
    vintage: '2026-05',
    status: 'disputed',
    provenance: 'D8 · S6 — deck QC breadth',
  },
  // D7 / S5 — Ball mill deck strip vs FD bands (complementary contexts)
  {
    id: 'cl-mill-rate-deck',
    kind: 'envelope',
    subject: { kind: 'constraint', id: 'u-grind', field: 'deck-strip-rate' },
    value: 143,
    valueLabel: '143 t/h deck strip',
    source: doc('src-pfd', 'flowsheet', undefined, 'equipment strip'),
    vintage: '2026-05',
    status: 'disputed',
    provenance: 'D7 · S5 — deck equipment strip',
  },
  {
    id: 'cl-mill-rate-fd',
    kind: 'envelope',
    subject: { kind: 'constraint', id: 'u-grind', field: 'fd-band-rate' },
    value: 75,
    valueLabel: '55–90 stph normal (design 75)',
    source: doc('src-dcs-fd', 'dcs-fd', undefined, 'Appendix B'),
    vintage: '2026-05',
    status: 'disputed',
    provenance: 'D7 · S5 — FD feed band',
  },
  {
    id: 'cl-mill-kw-deck',
    kind: 'envelope',
    subject: { kind: 'constraint', id: 'u-grind', field: 'deck-strip-power' },
    value: 1842,
    valueLabel: '1,842 kW deck strip',
    source: doc('src-pfd', 'flowsheet', undefined, 'equipment strip'),
    vintage: '2026-05',
    status: 'disputed',
    provenance: 'D7 · S5 — deck equipment strip',
  },
  {
    id: 'cl-mill-kw-fd',
    kind: 'envelope',
    subject: { kind: 'constraint', id: 'u-grind', field: 'fd-band-power' },
    value: 1050,
    valueLabel: '700–1,050 kW normal (DCS max 1,500)',
    source: doc('src-dcs-fd', 'dcs-fd', undefined, 'Appendix B'),
    vintage: '2026-05',
    status: 'disputed',
    provenance: 'D7 · S5 — FD power band',
  },
  // S7 — Zn pH live vs setpoint label (open — no ruling)
  {
    id: 'cl-znph-live',
    kind: 'design-basis',
    subject: { kind: 'kpi', id: 'zn-ph', field: 'live' },
    value: 10.2,
    valueLabel: '10.2 live',
    source: doc('src-pfd', 'flowsheet', undefined, 'CRO + alarms'),
    vintage: '2026-06',
    status: 'disputed',
    conflictsWith: ['cl-znph-sp'],
    provenance: 'S7 — CRO live value',
  },
  {
    id: 'cl-znph-sp',
    kind: 'design-basis',
    subject: { kind: 'kpi', id: 'zn-ph', field: 'live' },
    value: 10.8,
    valueLabel: '10.8 setpoint label',
    source: doc('src-pfd', 'flowsheet', undefined, 'Superintendent strip'),
    vintage: '2026-06',
    status: 'disputed',
    conflictsWith: ['cl-znph-live'],
    provenance: 'S7 — Superintendent strip label',
  },
  // S14 — Zn pH bands different vessels (open — no ruling; same authority tier → disputed).
  // The rougher-cell band leads: it is the Control Plan band `cp-zn-ph` (10.8–11.2),
  // the value this dispute rules to (polish.P18 adjudication 11), so the Rule
  // form opens on it.
  {
    id: 'cl-znph-rough',
    kind: 'verification',
    subject: { kind: 'constraint', id: 'zn-ph-band', field: 'limit' },
    value: { low: 10.8, high: 11.2 },
    valueLabel: '10.8–11.2 rougher cell',
    source: doc('src-pfd', 'flowsheet', undefined, 'rougher band'),
    vintage: '2026-05',
    status: 'disputed',
    conflictsWith: ['cl-znph-cond'],
    provenance: 'S14 · X2 — B rougher band (deck)',
  },
  {
    id: 'cl-znph-cond',
    kind: 'verification',
    subject: { kind: 'constraint', id: 'zn-ph-band', field: 'limit' },
    value: { low: 11.0, high: 12.0 },
    valueLabel: '11.0–12.0 conditioner',
    source: doc('src-dcs-fd', 'dcs-fd', undefined, '§8 conditioner band'),
    vintage: '2026-05',
    status: 'disputed',
    conflictsWith: ['cl-znph-rough'],
    provenance: 'S14 · X2 — A conditioner band (FD)',
  },
];

/** Mass-balance rev 2 claims added on Update ingest. */
export const REV2_CLAIMS: readonly Claim[] = [
  {
    id: 'cl-pb-grade-mb2',
    kind: 'design-basis',
    subject: { kind: 'stream-spec', id: 'st-pbcon', field: 'pb-grade' },
    value: 67.0,
    valueLabel: '67.0% Pb',
    source: doc('src-balance', 'mass-balance', 'rev 2'),
    vintage: '2026-06',
    status: 'disputed',
    conflictsWith: ['cl-pb-grade-deck'],
    provenance: 'mass balance rev 2 — D1 restated',
  },
];

// polish.P18 (#705, locked comp `ingestion-setup.html` B, adjudications 9–11):
// every seeded ruling is dated on or before the v2 cut (01 Jun 2026), so
// nothing after the 08 Jun cursor reads as an actual; `reason` is the plant
// reason the glass shows, and `provenance` keeps our own record (people,
// documents, ids) for the ProvenanceChip tooltip. The two concentrate-grade
// rulings accept the design basis every other surface reads (`rd-kpi-pb-grade`
// 67.0, `rd-kpi-zn-grade` 58.0; the Zn concentrate spec is > 55 % Zn), so the
// ruled seed no longer contradicts the Control Plan rows or the cross-route
// concentrate spec (discrepancy row 300).
export const RULINGS: readonly Ruling[] = [
  {
    id: 'rul-s1',
    subject: { kind: 'stream-spec', id: 'st-pbcon', field: 'pb-grade' },
    acceptsClaimId: 'cl-pb-grade-spec',
    author: { role: 'metallurgist', name: 'S. Park', seatId: 'seat-met' },
    decidedAt: '2026-05-28',
    reason: 'Design basis 67.0% Pb, the Control Plan floor; 65.0% and 63.4% stay on record.',
    provenance:
      "D1 · S1 — first ruled to the deck's 65.0 % column (the shipped dashboards read it); re-ruled to the module-doc spec in polish.P18 so the design basis agrees with rd-kpi-pb-grade and the Control Plan.",
  },
  {
    id: 'rul-s1-zn',
    subject: { kind: 'stream-spec', id: 'st-zncon', field: 'zn-grade' },
    acceptsClaimId: 'cl-zn-grade-spec',
    author: { role: 'metallurgist', name: 'S. Park', seatId: 'seat-met' },
    decidedAt: '2026-05-28',
    stage: 'approval',
    reason: 'Design basis 58.0% Zn, above the > 55% Zn concentrate spec.',
    provenance:
      "D2 · S1 — first ruled to the deck's 52.0 % column; re-ruled to the module-doc spec in polish.P18 (52.0 % sits below ConcentrateSpec cs-zn > 55 % Zn).",
  },
  {
    id: 'rul-s1-pb-rec',
    subject: { kind: 'kpi', id: 'pb-recovery', field: 'design' },
    acceptsClaimId: 'cl-pb-rec-deck',
    author: { role: 'metallurgist', name: 'S. Park', seatId: 'seat-met' },
    decidedAt: '2026-05-28',
    stage: 'approval',
    reason: 'PFD 87% Pb recovery as the interim design basis.',
    provenance:
      'Superseded by rul-pfs-pb-rec — the deck figure stays as disputed history; PFS 88.2 % is the numbers authority.',
  },
  {
    id: 'rul-pfs-pb-rec',
    subject: { kind: 'kpi', id: 'pb-recovery', field: 'design' },
    acceptsClaimId: 'cl-pb-rec-pfs',
    author: { role: 'metallurgist', name: 'S. Park', seatId: 'seat-met' },
    decidedAt: '2026-06-01',
    stage: 'approval',
    reason: 'PFS design basis 88.2% Pb recovery.',
    provenance: "Paul's PFS design basis — supersedes the deck-vs-C3 dispute for the live seed (seed.P4 #293).",
  },
  {
    id: 'rul-pfs-zn-rec',
    subject: { kind: 'kpi', id: 'zn-recovery', field: 'design' },
    acceptsClaimId: 'cl-zn-rec-pfs',
    author: { role: 'metallurgist', name: 'S. Park', seatId: 'seat-met' },
    decidedAt: '2026-06-01',
    stage: 'approval',
    reason: 'PFS design basis 85.1% Zn recovery.',
    provenance: "Paul's PFS design basis — supersedes the deck's 90.0 % for the live seed.",
  },
  {
    id: 'rul-pfs-ag-rec',
    subject: { kind: 'kpi', id: 'ag-recovery', field: 'design' },
    acceptsClaimId: 'cl-ag-rec-pfs',
    author: { role: 'metallurgist', name: 'S. Park', seatId: 'seat-met' },
    decidedAt: '2026-06-01',
    stage: 'approval',
    reason: 'PFS design basis 84.2% Ag recovery.',
    provenance: "Paul's PFS design basis.",
  },
  {
    id: 'rul-s2',
    subject: { kind: 'stream-spec', id: 'st-pbcon', field: 'regrind-p80' },
    acceptsClaimId: 'cl-regrind-deck',
    author: { role: 'metallurgist', name: 'S. Park', seatId: 'seat-met' },
    decidedAt: '2026-05-28',
    reason: '38 µm is the running regrind setpoint; 30 µm stays as the design note.',
    provenance: 'S2 — live setpoint from the deck; FD §7 design figure kept as a note.',
  },
  {
    id: 'rul-s4',
    subject: { kind: 'constraint', id: 'u-cyc', field: 'in-service' },
    acceptsClaimId: 'cl-cyc-spec',
    author: { role: 'metallurgist', name: 'S. Park', seatId: 'seat-met' },
    decidedAt: '2026-05-28',
    reason: '4 duty + 1 spare as specified; ~5.7 cyclones at stated duty only tightens the grinding constraint.',
    provenance: 'S4 — keep 4+1 (the spec); the FD aside would sharpen, not weaken, the bottleneck claim.',
  },
  {
    id: 'rul-s5',
    subject: { kind: 'constraint', id: 'u-grind', field: 'deck-strip-rate' },
    acceptsClaimId: 'cl-mill-rate-deck',
    author: { role: 'metallurgist', name: 'S. Park', seatId: 'seat-met' },
    decidedAt: '2026-05-28',
    reason: '143 t/h is the mill strip rate; the FD bands set the operating window.',
    provenance: 'S5 — deck numbers on deck-derived screens, doc bands on the Plant model; the conflict stays visible.',
  },
];

function winnerClaimIds(
  claims: readonly Claim[],
  rulings: readonly Ruling[],
): string[] {
  const { winners } = resolve(claims, rulings, AUTHORITY);
  return [...winners.values()].map((c) => c.id);
}

const v1Winners = winnerClaimIds(
  CLAIMS.filter((c) => c.vintage <= '2026-05'),
  [],
);

const v2Winners = winnerClaimIds(CLAIMS, RULINGS);

export const MODEL_VERSIONS: readonly ResolvedVersion[] = [
  {
    id: 'pm-v1',
    scope: 'plant-model',
    n: 1,
    acceptedAt: '2026-05-15',
    acceptedBy: { role: 'metallurgist', name: 'Met' },
    claimIds: v1Winners,
    note: 'Functional description + flowsheet — pre-reconciliation',
  },
  {
    id: 'pm-v2',
    scope: 'plant-model',
    n: 2,
    acceptedAt: '2026-06-01',
    acceptedBy: { role: 'metallurgist', name: 'Met' },
    claimIds: v2Winners,
    supersedes: 'pm-v1',
    // tim-scope §2.4 rulings (docs/audits/2026-08-19-tim-scope-reconciliation.md)
    note: 'After mass-balance rev 2 and rulings',
  },
];

export function claimsForSession(revisedBalance: boolean): readonly Claim[] {
  if (!revisedBalance) return CLAIMS;
  const extra = REV2_CLAIMS.filter(
    (c) => !CLAIMS.some((existing) => existing.id === c.id),
  );
  return [...CLAIMS, ...extra];
}

export function subjectLabel(subject: { kind: string; id: string; field?: string }): string {
  const fieldLabels: Record<string, string> = {
    'pb-grade': 'Pb grade',
    'zn-grade': 'Zn grade',
    'ag-grade': 'Ag grade',
    'regrind-p80': 'regrind P80',
    p80: 'P80',
    design: 'design',
    live: 'live',
    'loop-tag': 'loop tag',
    'instrument-tag': 'instrument tag',
    'in-service': 'in service',
    'fd-core': 'FD core',
    'deck-register': 'deck register',
    limit: 'pH band',
    'deck-strip-rate': 'deck strip rate',
    'fd-band-rate': 'FD rate band',
    'deck-strip-power': 'deck strip power',
    'fd-band-power': 'FD power band',
  };
  const streamNames: Record<string, string> = {
    'st-pbcon': 'Pb concentrate',
    'st-zncon': 'Zn concentrate',
    'st-cyc-of': 'Cyclone overflow',
  };
  const kpiNames: Record<string, string> = {
    'zn-recovery': 'Zn recovery',
    'pb-recovery': 'Pb recovery',
    'ag-recovery': 'Ag recovery',
    'zn-ph': 'Zn pH',
  };
  const constraintNames: Record<string, string> = {
    'u-cyc': 'Cyclone duty',
    'u-grind': 'Ball mill',
    'zn-ph-band': 'Zn pH band',
  };
  const tagNames: Record<string, string> = {
    'pb-rougher-level': 'Pb rougher level',
  };
  let base = subject.id;
  if (subject.kind === 'stream-spec') base = streamNames[subject.id] ?? subject.id;
  if (subject.kind === 'kpi') base = kpiNames[subject.id] ?? subject.id;
  if (subject.kind === 'constraint') base = constraintNames[subject.id] ?? subject.id;
  if (subject.kind === 'tag') base = tagNames[subject.id] ?? subject.id;
  if (subject.kind === 'sample-point') base = 'Sampling inventory';
  if (subject.field) {
    const field = fieldLabels[subject.field] ?? subject.field;
    return `${base} · ${field}`;
  }
  return base;
}

const SOURCE_DOC_LABEL: Record<string, string> = {
  'src-dcs-fd': 'DCS FD',
  'src-balance': 'Mass balance',
  'src-pfd': 'PFD',
  'src-io': 'I/O list',
  'src-fmea': 'FMEA',
  'src-pid': 'P&ID',
};

export function sourceChipLabel(claim: Claim): string {
  const src = claim.source;
  if (src.tier !== 'document') return src.tier;
  const base = SOURCE_DOC_LABEL[src.sourceId] ?? src.sourceKind;
  const rev =
    src.revision ??
    (src.sourceId === 'src-dcs-fd' ? 'rev B' : undefined);
  return rev ? `${base} ${rev}` : base;
}

export { subjectKey };
